import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Star,
  DollarSign,
  Calendar,
  Shield
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EnhancedPEOQuote, ComparisonMetrics } from '@/types/quotes-enhanced.types';

interface QuoteComparisonMatrixProps {
  quotes: EnhancedPEOQuote[];
  metrics: ComparisonMetrics[];
  onViewDetails: (quoteId: string) => void;
  onRespond: (quoteId: string, action: 'accept' | 'decline' | 'interested') => void;
}

export const QuoteComparisonMatrix: React.FC<QuoteComparisonMatrixProps> = ({
  quotes,
  metrics,
  onViewDetails,
  onRespond
}) => {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [sortBy, setSortBy] = useState<'savings' | 'cost' | 'score'>('score');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getSortedMetrics = () => {
    return [...metrics].sort((a, b) => {
      switch (sortBy) {
        case 'savings':
          return b.savings_amount - a.savings_amount;
        case 'cost':
          return a.total_cost - b.total_cost;
        case 'score':
        default:
          return b.recommendation_score - a.recommendation_score;
      }
    });
  };

  const getQuoteByMetric = (metric: ComparisonMetrics) => {
    return quotes.find(q => q.id === metric.quote_id);
  };

  const getRecommendationBadge = (score: number) => {
    if (score >= 80) return { label: 'Highly Recommended', variant: 'default' as const };
    if (score >= 60) return { label: 'Recommended', variant: 'secondary' as const };
    if (score >= 40) return { label: 'Consider', variant: 'outline' as const };
    return { label: 'Review Needed', variant: 'destructive' as const };
  };

  const sortedMetrics = getSortedMetrics();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Quote Comparison</h2>
          <p className="text-muted-foreground">Compare your PEO quotes side-by-side</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <Button
            variant={sortBy === 'score' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('score')}
          >
            <Star className="h-4 w-4 mr-1" />
            Score
          </Button>
          <Button
            variant={sortBy === 'savings' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('savings')}
          >
            <TrendingUp className="h-4 w-4 mr-1" />
            Savings
          </Button>
          <Button
            variant={sortBy === 'cost' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('cost')}
          >
            <DollarSign className="h-4 w-4 mr-1" />
            Cost
          </Button>
        </div>
      </div>

      {/* Comparison Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {sortedMetrics.map((metric, index) => {
          const quote = getQuoteByMetric(metric);
          if (!quote) return null;

          const recommendation = getRecommendationBadge(metric.recommendation_score);
          const isTopChoice = index === 0;

          return (
            <Card key={quote.id} className={cn(
              "relative transition-all duration-200 hover:shadow-lg",
              isTopChoice && "ring-2 ring-primary shadow-lg"
            )}>
              {isTopChoice && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">
                    <Star className="h-3 w-3 mr-1 fill-current" />
                    Best Match
                  </Badge>
                </div>
              )}

              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{metric.provider_name}</CardTitle>
                    <CardDescription>
                      Score: {metric.recommendation_score}/100
                    </CardDescription>
                  </div>
                  <Badge variant={recommendation.variant}>
                    {recommendation.label}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Cost Overview */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Annual Cost</p>
                    <p className="font-semibold">{formatCurrency(metric.total_cost)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Savings</p>
                    <div className="flex items-center gap-1">
                      {metric.savings_amount > 0 ? (
                        <>
                          <TrendingUp className="h-4 w-4 text-green-500" />
                          <span className="font-semibold text-green-600">
                            {formatCurrency(metric.savings_amount)}
                          </span>
                        </>
                      ) : metric.savings_amount < 0 ? (
                        <>
                          <TrendingDown className="h-4 w-4 text-red-500" />
                          <span className="font-semibold text-red-600">
                            {formatCurrency(Math.abs(metric.savings_amount))} more
                          </span>
                        </>
                      ) : (
                        <span className="font-semibold text-muted-foreground">
                          No change
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Service Coverage</span>
                    <span className="font-medium">{metric.service_score}%</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Implementation</span>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span className="font-medium">{metric.implementation_days} days</span>
                    </div>
                  </div>
                </div>

                {/* Quick Service Overview */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">Included Services</p>
                  <div className="flex flex-wrap gap-1">
                    {quote.line_items.filter(item => item.is_included).slice(0, 3).map(item => (
                      <Badge key={item.id} variant="secondary" className="text-xs">
                        {item.service_name}
                      </Badge>
                    ))}
                    {quote.line_items.filter(item => item.is_included).length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{quote.line_items.filter(item => item.is_included).length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(quote.id)}
                    className="flex-1"
                  >
                    View Details
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onRespond(quote.id, 'interested')}
                    className="flex-1"
                  >
                    Show Interest
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Comparison</CardTitle>
          <CardDescription>
            Side-by-side comparison of all quote details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="costs">Costs</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Provider</th>
                      <th className="text-left p-2">Annual Cost</th>
                      <th className="text-left p-2">Savings</th>
                      <th className="text-left p-2">Score</th>
                      <th className="text-left p-2">Implementation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMetrics.map(metric => (
                      <tr key={metric.quote_id} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{metric.provider_name}</td>
                        <td className="p-2">{formatCurrency(metric.total_cost)}</td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            {metric.savings_amount > 0 ? (
                              <>
                                <TrendingUp className="h-3 w-3 text-green-500" />
                                <span className="text-green-600">
                                  {formatCurrency(metric.savings_amount)}
                                </span>
                              </>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          <Badge variant="secondary">
                            {metric.recommendation_score}/100
                          </Badge>
                        </td>
                        <td className="p-2">{metric.implementation_days} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="costs" className="mt-6">
              <div className="space-y-4">
                {sortedMetrics.map(metric => {
                  const quote = getQuoteByMetric(metric);
                  if (!quote) return null;

                  return (
                    <Card key={quote.id}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">{metric.provider_name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {quote.line_items.map(item => (
                            <div key={item.id} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{item.service_name}</span>
                                {item.is_included ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                              {item.proposed_cost && (
                                <p className="text-xs text-muted-foreground">
                                  {formatCurrency(item.proposed_cost)}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="services" className="mt-6">
              <div className="space-y-4">
                {sortedMetrics.map(metric => {
                  const quote = getQuoteByMetric(metric);
                  if (!quote) return null;

                  const includedServices = quote.line_items.filter(item => item.is_included);
                  const optionalServices = quote.line_items.filter(item => item.is_optional);

                  return (
                    <Card key={quote.id}>
                      <CardHeader>
                        <CardTitle className="text-lg">{metric.provider_name}</CardTitle>
                        <CardDescription>
                          {includedServices.length} included • {optionalServices.length} optional
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-6 md:grid-cols-2">
                          <div>
                            <h4 className="font-medium text-green-600 mb-3">✓ Included Services</h4>
                            <div className="space-y-2">
                              {includedServices.map(service => (
                                <div key={service.id} className="flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                  <span className="text-sm">{service.service_name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-amber-600 mb-3">? Optional Services</h4>
                            <div className="space-y-2">
                              {optionalServices.map(service => (
                                <div key={service.id} className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-amber-500" />
                                  <span className="text-sm">{service.service_name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="timeline" className="mt-6">
              <div className="space-y-4">
                {sortedMetrics.map(metric => {
                  const quote = getQuoteByMetric(metric);
                  if (!quote || !quote.implementation_timeline.length) return null;

                  return (
                    <Card key={quote.id}>
                      <CardHeader>
                        <CardTitle className="text-lg">{metric.provider_name}</CardTitle>
                        <CardDescription>
                          {metric.implementation_days} days total implementation
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {quote.implementation_timeline.map((milestone, index) => (
                            <div key={milestone.id} className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{milestone.milestone_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {milestone.estimated_duration_days} days
                                  {milestone.is_critical_path && (
                                    <Badge variant="destructive" className="ml-2 text-xs">
                                      Critical Path
                                    </Badge>
                                  )}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};